import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AccountInfoActions } from './account-info-header/actions';
import { IntegrationService } from './shared/services/integration.service';
import { APP_SETTINGS } from '@charlesschwab/as-ui-core';
import { AuthorizeActions } from './authorize/actions';
import { authorizeRequest } from './authorize/models/authorize-request';
import { Router } from '@angular/router';
import { displayCancelModal } from './shared/actions/shared.actions';
import { selectCreateTypeInContext, selectViewInContext } from './shared/reducers';
import { CreateTypes, pageInContext } from './shared/utilities/Constants';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { selectTransactionType } from './create-request/reducers';
import * as fromStandingAuthorizations from './standing-authorizations/reducers';
import { FeatureFlagService } from './shared/services/feature-flag-service';
import { UserPreferenceActions } from './user-preference/actions';

@Component({
  standalone: false,
  selector: 'money-movement-ui-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})

export class AppComponent implements OnInit, OnDestroy {
  title = 'money-movement-ui';
  containerEl: HTMLElement | null;
  mainNavEl: Element | null;
  step: string;
  createTypeInContext: CreateTypes;
  displayCancelModal: boolean;
  destroy$ = new Subject<void>();
  featureFlagsReady: boolean;

  constructor(
    private integrationService: IntegrationService,
    private store: Store,
    private router: Router,
    private featureFlagService: FeatureFlagService,
    @Inject(APP_SETTINGS) private settings?: any
  ) { }

  private singleSpaRoutingEventListener = () => {
    setTimeout(() => {
      const currentUrl = window.location.pathname + window.location.search;
      if (this.router.url !== currentUrl) {
        this.router.navigateByUrl(currentUrl, { replaceUrl: true });
      }
    }, 100);
  }

  ngOnInit() {
    /* There is code in the monolith SchwabCore.js file that hijacks the default browser behavior of showing a standard tooltip
        on mouseover when there is a title attribute on the html element. It will display a stylized (and slightly buggy) tooltip
        instead by copying the 'title' value to tooltip content and then setting the 'title' to empty string

        This catches events within our Money Movemeny UI MFE and stops their propagation so that the default browser behavior is maintained
    */
    this.containerEl = document.getElementById('money-movement-container');
    this.containerEl?.addEventListener('mouseover', stopTooltipPropagation);

    this.featureFlagService.featureServiceReady$.pipe(takeUntil(this.destroy$)).subscribe(isReady => {
      this.featureFlagsReady = isReady;
      if (isReady) {
        this.integrationService.hideSpinner();
      }
    });

    if (!(window.location.href)?.includes('si2')) { //if not monolith URL
      window.addEventListener('single-spa:routing-event', this.singleSpaRoutingEventListener);
    }

    this.integrationService.accountUpdated$.subscribe((e: CustomEvent<{ accountId: string }>) => {
      if (e.detail.accountId) {
        this.store.dispatch(AccountInfoActions.updateSubAccount({ accountId: e.detail.accountId }));
      }
    });
    this.store.dispatch(AuthorizeActions.authorize({ authorizeRequest: authorizeRequest }));

    this.store.select(selectViewInContext).pipe(takeUntil(this.destroy$)).subscribe((step) => {
      this.step = step;
    });

    this.store.select(selectCreateTypeInContext).pipe(takeUntil(this.destroy$)).subscribe((type) => {
      this.createTypeInContext = type;
    });

    this.store.dispatch(UserPreferenceActions.loadPreferences());

    combineLatest([
      this.store.select(selectTransactionType),
      this.store.select(fromStandingAuthorizations.selectTransactionType)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([transactionType, standingAuthTransactionType]) => {
      this.displayCancelModal = !!transactionType || !!standingAuthTransactionType;
    });

    // handle L1 navigation
    let elements = document.getElementsByClassName('mainNav');
    if (elements && elements.length) {
      this.mainNavEl = elements[0];
      this.mainNavEl.addEventListener('click', (e) => {
        if (this.displayCancelModal && (this.step === pageInContext.input || this.step === pageInContext.review)) {
          let el = document.querySelector('.focus');
          if (el && !el.classList.contains('moreMenu')) {
            this.dispatchCancelModal(e, el);
          } else {
            let moreMenuContentElements = document.getElementsByClassName('moreMenuContent');
            if (moreMenuContentElements) {
              let moreMenuContentEl = moreMenuContentElements[0];
              moreMenuContentEl.addEventListener('click', e2 => {
                var el2 = (e2.target as Element)?.parentElement?.parentElement;
                if (el2) {
                  let innerHtml = el2?.innerHTML;
                  if (!innerHtml.includes('target=\"_blank\"')) {
                    this.dispatchCancelModal(e2, el2);
                  }
                }
              }, false)
            }
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.containerEl?.removeEventListener('mouseover', stopTooltipPropagation);
    window.removeEventListener('single-spa:routing-event', this.singleSpaRoutingEventListener);
    this.destroy$.next();
    this.destroy$.complete();
  }

  dispatchCancelModal(event: Event, element: Element) {
    event.preventDefault();
    let innerHtml = element?.innerHTML;
    let nextUrlState: string = '';
    if (innerHtml) {
      let start = innerHtml.indexOf('href');
      let end = innerHtml.indexOf('"', start + 6);
      nextUrlState = innerHtml.substring(start + 6, end);
    }

    if (this.createTypeInContext === 'transaction') {
      this.store.dispatch(
        displayCancelModal({
          nextUrlState: nextUrlState,
          title: 'Cancel transaction in progress',
          content: ['Canceling will reset your transaction inputs. Are you sure you want to continue?'],
          greenButton: 'Cancel transaction',
          grayButton: 'Back',
          isL1Nav: true,
          source: 'mainNavRedirect'
        })
      );
    } else {
      this.store.dispatch(
        displayCancelModal({
          nextUrlState: nextUrlState,
          title: 'Cancel this work in progress?',
          content: ['The information you have entered to create this standing authorization will be lost.'],
          greenButton: 'Cancel standing authorization',
          grayButton: 'Back',
          isL1Nav: true,
          source: 'mainNavRedirect'
        })
      );
    }
  }
}

function stopTooltipPropagation(e: any) {
  if (e.target.attributes.title) {
    e.stopPropagation();
  }
}
